import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { loadSettings, saveSettings, BACKGROUNDS, type GameSettings, type BackgroundId } from '@/config/settings';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { setDiscordIdlePresence } from '@/discord';
import { createBackButton } from '@/ui/buttons';
import { BindingFormModal } from '@/ui/Modal';
import { formatBinding } from '@/ui/format';
import { AudioManager } from '@/systems/AudioManager';

export class SettingsScene extends Phaser.Scene {
  private settings!: GameSettings;
  private inputKeyText!: Phaser.GameObjects.Text;
  private sfxSlider!: Phaser.GameObjects.Rectangle;
  private sfxFill!: Phaser.GameObjects.Rectangle;
  private musicSlider!: Phaser.GameObjects.Rectangle;
  private musicFill!: Phaser.GameObjects.Rectangle;
  private audioManager!: AudioManager;
  private modal: BindingFormModal | null = null;

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create(): void {
    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Discord Activity presence (menus/idle)
    setDiscordIdlePresence();

    // Arcade background
    drawArcadeBackground(this);

    this.settings = loadSettings();

    // Background panel (same style as JoinScene/LevelSelectScene)
    const panelX = GAME_WIDTH / 2;
    const panelY = GAME_HEIGHT / 2 + 30;
    const panelWidth = 700;
    const panelHeight = 440;
    const panel = this.add.rectangle(panelX, panelY, panelWidth, panelHeight, THEME.colors.backgroundLightHex, 0.9);
    panel.setStrokeStyle(3, THEME.colors.magentaHex);

    // Title with glow (positioned above panel)
    const title = this.add.text(GAME_WIDTH / 2, panelY - panelHeight / 2 - 40, 'SETTINGS', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.title,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 15);

    // Panel top edge for reference
    const panelTop = panelY - panelHeight / 2;

    // Key bindings section
    this.createKeyBindingsSection(panelTop);

    this.audioManager = AudioManager.get(this);

    // Volume section
    this.createVolumeSection(panelTop);

    // Background section
    this.createBackgroundSection(panelTop);

    // Back button
    createBackButton(this, {
      x: 100,
      y: 60,
      onClick: () => {
        this.transitionTo('MenuScene');
      },
    });
  }

  private createKeyBindingsSection(panelTop: number): void {
    const startY = panelTop + 60;

    // Section title
    this.add.text(GAME_WIDTH / 2, startY, 'KEY BINDING', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Input key label
    this.add.text(GAME_WIDTH / 2 - 150, startY + 30, 'Input Key', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    // Display current binding
    const binding = this.settings.inputBindings[0] ?? { playerId: 0, type: 'keyboard', key: this.settings.inputKey };
    this.inputKeyText = this.add.text(GAME_WIDTH / 2 - 30, startY + 34, formatBinding(binding), {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.cyan,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0, 0.5);

    // EDIT button that opens the modal
    const editBtn = this.add.rectangle(GAME_WIDTH / 2 + 130, startY + 34, 100, 40, THEME.colors.backgroundPanelHex, 1);
    editBtn.setStrokeStyle(2, THEME.colors.cyanHex);
    editBtn.setInteractive({ useHandCursor: true });

    const editText = this.add.text(GAME_WIDTH / 2 + 130, startY + 34, 'EDIT', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);

    editBtn.on('pointerover', () => editBtn.setFillStyle(THEME.colors.backgroundLightHex, 1));
    editBtn.on('pointerout', () => editBtn.setFillStyle(THEME.colors.backgroundPanelHex, 1));
    editBtn.on('pointerdown', () => this.openBindingModal());

    // Explanation text
    this.add.text(GAME_WIDTH / 2, startY + 80, 'TAP for dot, HOLD for dash', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);
  }

  private createVolumeSection(panelTop: number): void {
    // Position relative to panel top
    const startY = panelTop + 180;

    this.add.text(GAME_WIDTH / 2, startY, 'VOLUME', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // SFX volume
    this.add.text(GAME_WIDTH / 2 - 150, startY + 50, 'Sound Effects', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    const sfxSliderResult = this.createSlider(
      GAME_WIDTH / 2 + 80,
      startY + 50,
      this.settings.sfxVolume,
      (value) => {
        this.settings.sfxVolume = value;
        saveSettings(this.settings);
        this.audioManager.updateVolume(value, undefined);
      }
    );
    this.sfxSlider = sfxSliderResult.track;
    this.sfxFill = sfxSliderResult.fill;

    // Music volume
    this.add.text(GAME_WIDTH / 2 - 150, startY + 100, 'Music', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    const musicSliderResult = this.createSlider(
      GAME_WIDTH / 2 + 80,
      startY + 100,
      this.settings.musicVolume,
      (value) => {
        this.settings.musicVolume = value;
        saveSettings(this.settings);
        this.audioManager.updateVolume(undefined, value);
      }
    );
    this.musicSlider = musicSliderResult.track;
    this.musicFill = musicSliderResult.fill;
  }

  private createBackgroundSection(panelTop: number): void {
    // Position relative to panel top
    const startY = panelTop + 310;

    this.add.text(GAME_WIDTH / 2, startY, 'BACKGROUND', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    const buttonWidth = 100;
    const spacing = 20;
    const totalWidth = BACKGROUNDS.length * buttonWidth + (BACKGROUNDS.length - 1) * spacing;
    const startX = (GAME_WIDTH - totalWidth) / 2 + buttonWidth / 2;

    BACKGROUNDS.forEach((bg, index) => {
      const x = startX + index * (buttonWidth + spacing);
      const y = startY + 50;
      const isSelected = this.settings.background === bg.id;

      const btnBg = this.add.rectangle(x, y, buttonWidth, 40, THEME.colors.backgroundPanelHex, 1);
      btnBg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      btnBg.setInteractive({ useHandCursor: true });

      this.add.text(x, y, bg.label.toUpperCase(), {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.tiny,
        color: isSelected ? THEME.colors.text : THEME.colors.textMuted,
      }).setOrigin(0.5);

      btnBg.on('pointerover', () => {
        if (!isSelected) {
          btnBg.setStrokeStyle(2, THEME.colors.cyanHex);
          btnBg.setFillStyle(THEME.colors.backgroundLightHex, 1);
        }
      });
      btnBg.on('pointerout', () => {
        if (!isSelected) {
          btnBg.setStrokeStyle(2, THEME.colors.borderHex);
          btnBg.setFillStyle(THEME.colors.backgroundPanelHex, 1);
        }
      });
      btnBg.on('pointerdown', () => {
        this.selectBackground(bg.id);
      });
    });
  }

  private selectBackground(id: BackgroundId): void {
    this.settings.background = id;
    saveSettings(this.settings);
    // Restart scene to show new background
    this.scene.restart();
  }

  private createSlider(
    x: number,
    y: number,
    initialValue: number,
    onChange: (value: number) => void
  ): { track: Phaser.GameObjects.Rectangle; fill: Phaser.GameObjects.Rectangle } {
    const width = 150;
    const height = 20;

    // Track background
    const track = this.add.rectangle(x, y, width, height, THEME.colors.backgroundPanelHex, 1);
    track.setStrokeStyle(2, THEME.colors.borderHex);
    track.setOrigin(0, 0.5);

    // Fill
    const fill = this.add.rectangle(x + 2, y, (width - 4) * initialValue, height - 4, THEME.colors.primaryHex, 1);
    fill.setOrigin(0, 0.5);

    // Value text
    const valueText = this.add.text(x + width + 15, y, `${Math.round(initialValue * 100)}%`, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    // Make interactive
    track.setInteractive({ useHandCursor: true });

    const updateSlider = (pointerX: number) => {
      const relativeX = pointerX - x;
      const value = Phaser.Math.Clamp(relativeX / width, 0, 1);
      fill.width = (width - 4) * value;
      valueText.setText(`${Math.round(value * 100)}%`);
      onChange(value);
    };

    track.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      updateSlider(pointer.x);
    });

    track.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        updateSlider(pointer.x);
      }
    });

    return { track, fill };
  }

  private transitionTo(scene: string, data?: object): void {
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }

  private openBindingModal(): void {
    if (this.modal) return; // Already open

    const currentBinding = this.settings.inputBindings[0] ?? {
      playerId: 0,
      type: 'keyboard' as const,
      key: this.settings.inputKey,
    };

    this.modal = new BindingFormModal({
      scene: this,
      title: 'CHANGE KEY BINDING',
      hint: 'Press a key or click LMB/RMB to bind',
      playerName: '', // Not used when showNameField is false
      currentBinding,
      showNameField: false, // Hide name field for settings use
      onSave: (_name, binding) => {
        this.settings.inputBindings = [binding];
        if (binding.type === 'keyboard') {
          this.settings.inputKey = binding.key;
        }
        saveSettings(this.settings);
        this.inputKeyText.setText(formatBinding(binding));
        this.modal = null;
      },
      onCancel: () => {
        this.modal = null;
      },
      onDestroy: () => {
        this.modal = null;
      },
    });
  }

  shutdown(): void {
    if (this.modal) {
      this.modal.destroy();
      this.modal = null;
    }
  }
}
